import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useOrganization } from "@/hooks/useOrganization";
import { useEmailTemplates, type _EmailTemplate } from "@/hooks/useEmailTemplates";
import { templateMeta, defaultTemplates, exampleData, renderTemplate } from "@/lib/defaultEmailTemplates";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronDown, ChevronRight, Save, RotateCcw, Eye, Mail } from "lucide-react";
import { toast } from "sonner";

interface Props {
  settings: Record<string, unknown>;
  updateSetting: (key: string, value: unknown) => void;
}

// Group template types
const templateGroups = [
  {
    labelKey: "auth",
    types: ["signup", "recovery", "magiclink", "invite", "email_change"],
  },
  {
    labelKey: "booking",
    types: ["booking_confirmation", "booking_cancellation", "booking_reminder", "waitlist_offer"],
  },
  {
    labelKey: "tournaments",
    types: ["tournament_invite", "tournament_result"],
  },
  {
    labelKey: "training",
    types: ["training_reminder", "training_cancelled", "lesson_confirmation", "lesson_reminder"],
  },
  {
    labelKey: "leagues",
    types: ["league_match_scheduled", "league_result_reminder"],
  },
];

export default function SettingsNotifications({ settings, updateSetting }: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { templates, isLoading, upsertTemplate, resetTemplate, isSaving } = useEmailTemplates(organization?.id);
  const [expandedType, setExpandedType] = useState<string | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");
  const [previewType, setPreviewType] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const notif = (settings.notifications as Record<string, unknown>) || {};
  const update = (key: string, value: unknown) => {
    updateSetting("notifications", { ...notif, [key]: value });
  };

  const handleExpand = (type: string) => {
    if (expandedType === type) {
      setExpandedType(null);
      return;
    }
    const tmpl = templates.find((t) => t.type === type);
    if (tmpl) {
      setEditSubject(tmpl.subject);
      setEditBody(tmpl.body);
    }
    setExpandedType(type);
  };

  const handleToggle = async (type: string, active: boolean) => {
    const tmpl = templates.find((t) => t.type === type);
    if (!tmpl) return;
    try {
      await upsertTemplate({
        type,
        subject: tmpl.subject,
        body: tmpl.body,
        is_active: active,
      });
      toast.success(active ? t("settings.notifications.templateActivated") : t("settings.notifications.templateDeactivated"));
    } catch {
      toast.error(t("toast.couldNotUpdateTemplate"));
    }
  };

  const handleSave = async (type: string) => {
    const tmpl = templates.find((t) => t.type === type);
    if (!tmpl) return;
    try {
      await upsertTemplate({
        type,
        subject: editSubject,
        body: editBody,
        is_active: tmpl.is_active,
      });
      toast.success(t("toast.templateSaved"));
    } catch {
      toast.error(t("toast.couldNotSaveTemplate"));
    }
  };

  const handleReset = async (type: string) => {
    try {
      await resetTemplate(type);
      const def = defaultTemplates[type];
      if (def) {
        setEditSubject(def.subject);
        setEditBody(def.body);
      }
      toast.success(t("toast.templateReset"));
    } catch {
      toast.error(t("toast.couldNotResetTemplate"));
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = bodyRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editBody;
    const insert = `{${variable}}`;
    setEditBody(text.substring(0, start) + insert + text.substring(end));
    // Refocus after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + insert.length, start + insert.length);
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  // Preview template with example data + real club name
  const realExampleData = { ...exampleData, clubName: organization?.name ?? exampleData.clubName };
  const previewTmpl = previewType ? templates.find((t) => t.type === previewType) : null;
  const previewSubject = previewTmpl ? renderTemplate(
    expandedType === previewType ? editSubject : previewTmpl.subject,
    realExampleData
  ) : "";
  const previewBody = previewTmpl ? renderTemplate(
    expandedType === previewType ? editBody : previewTmpl.body,
    realExampleData
  ) : "";

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.notifications.title")}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t("settings.notifications.description")}
        </p>
      </div>

      {/* Chat forwarding */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{t("settings.notifications.forwardChat")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.notifications.forwardChatDesc")}
              </p>
            </div>
            <Switch
              checked={notif.chat_forward_enabled ?? false}
              onCheckedChange={(v) => update("chat_forward_enabled", v)}
            />
          </div>
          {(notif.chat_forward_enabled ?? false) && (
            <div className="space-y-1.5 pt-1">
              <Label className="text-xs">{t("settings.notifications.forwardEmail")}</Label>
              <Input
                type="email"
                value={notif.chat_forward_email ?? ""}
                onChange={(e) => update("chat_forward_email", e.target.value)}
                placeholder="klubb@example.com"
                className="h-8 text-sm"
              />
              <p className="text-[10px] text-muted-foreground">
                {t("settings.notifications.forwardEmailHint")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Global notification settings */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground">{t("settings.notifications.general")}</h4>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">{t("settings.notifications.reminderHoursBefore")}</Label>
              <p className="text-xs text-muted-foreground">{t("settings.notifications.reminderHoursBeforeDesc")}</p>
            </div>
            <NumberInput
              min={1}
              max={48}
              value={notif.reminder_hours_before ?? 2}
              onValueChange={(v) => update("reminder_hours_before", v || 2)}
              className="w-20 h-8 text-sm"
            />
          </div>
        </CardContent>
      </Card>

      {/* Template groups */}
      {templateGroups.map((group) => (
        <div key={group.labelKey} className="space-y-2">
          <h4 className="text-xs font-bold text-muted-foreground tracking-wider">{t(`settings.notifications.group_${group.labelKey}`)}</h4>
          <div className="space-y-1.5">
            {group.types.map((type) => {
              const meta = templateMeta[type];
              const tmpl = templates.find((t) => t.type === type);
              if (!meta || !tmpl) return null;
              const isExpanded = expandedType === type;
              const isModified = !tmpl.isDefault;

              return (
                <Collapsible key={type} open={isExpanded}>
                  <div className="rounded-lg border bg-card overflow-hidden">
                    {/* Header row */}
                    <CollapsibleTrigger asChild>
                      <div
                        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => handleExpand(type)}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{meta.label}</span>
                            {isModified && (
                              <Badge variant="outline" className="text-[10px] h-4 px-1">{t("settings.notifications.customized")}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{meta.desc}</p>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <Switch
                            checked={tmpl.is_active}
                            onCheckedChange={(v) => handleToggle(type, v)}
                          />
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    {/* Expanded editor */}
                    <CollapsibleContent>
                      <div className="border-t p-4 space-y-4 bg-muted/10">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">{t("settings.notifications.fieldSubject")}</Label>
                          <Input
                            value={editSubject}
                            onChange={(e) => setEditSubject(e.target.value)}
                            className="text-sm"
                            placeholder={t("settings.notifications.placeholderSubject")}
                          />
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium">{t("settings.notifications.fieldBody")}</Label>
                          <Textarea
                            ref={bodyRef}
                            value={editBody}
                            onChange={(e) => setEditBody(e.target.value)}
                            rows={8}
                            className="text-sm font-mono"
                            placeholder={t("settings.notifications.placeholderBody")}
                          />
                        </div>

                        {/* Variable badges */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-muted-foreground">{t("settings.notifications.availableVariables")}</Label>
                          <div className="flex flex-wrap gap-1.5">
                            {meta.variables.map((v) => (
                              <Badge
                                key={v}
                                variant="secondary"
                                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-xs"
                                onClick={() => insertVariable(v)}
                              >
                                {`{${v}}`}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-1">
                          <Button
                            size="sm"
                            className="gap-1.5"
                            onClick={() => handleSave(type)}
                            disabled={isSaving}
                          >
                            <Save className="h-3.5 w-3.5" />
                            {t("settings.notifications.btnSave")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1.5"
                            onClick={() => setPreviewType(type)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            {t("settings.notifications.btnPreview")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="gap-1.5 text-muted-foreground"
                            onClick={() => handleReset(type)}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            {t("settings.notifications.btnReset")}
                          </Button>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>
        </div>
      ))}

      {/* Preview dialog */}
      <Dialog open={!!previewType} onOpenChange={(open) => { if (!open) setPreviewType(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              {t("settings.notifications.previewTitle")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-background">
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">{t("settings.notifications.previewSubject")}:</p>
                  <p className="font-semibold text-sm">{previewSubject}</p>
                </div>
                <div className="border-t pt-3">
                  <p className="text-xs text-muted-foreground mb-1">{t("settings.notifications.previewBody")}:</p>
                  <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{previewBody}</pre>
                </div>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground">
              {t("settings.notifications.previewHint")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
